import React from 'react';
import KI from '../theme/palette';
import { IcCalendar } from '../theme/icons';

// « CA par jour de la semaine » drawn INSIDE the same dark beveled navy case
// as SalesTrendChart / PurchaseSalesBarChart, so it can live in the same card
// as the Évolution chart and switch with it. Two modes: 'bars' | 'curve'.
// Static render (no draw-in animations — owner rule on the stats charts).
//
// `rows` = the statistics API's by_weekday: [{weekday: 1..7 (1=dimanche), ca, count}]
const WEEKDAYS = [
  { wd: 2, label: 'Lun' }, { wd: 3, label: 'Mar' }, { wd: 4, label: 'Mer' },
  { wd: 5, label: 'Jeu' }, { wd: 6, label: 'Ven' }, { wd: 7, label: 'Sam' },
  { wd: 1, label: 'Dim' },
];

const COLOR = KI.spruce; // même vert #15803D que la série CA (Brand Kit §05)

// viewBox units — same canvas as the other stats charts so the card keeps
// exactly the same size and background when switching.
const W = 1000;
// H = prop `height` (défaut 460), même logique que SalesTrendChart — les deux
// graphes de la carte Statistiques partagent la même valeur pour garder un cadre
// identique. PLOT est calculé dans le composant.
const M = { top: 22, right: 22, bottom: 46, left: 70 };

const fmtTick = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(a % 1_000_000 ? 1 : 0)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(a % 1_000 ? 1 : 0)}k`;
  return `${Math.round(v)}`;
};

const niceNum = (range, round) => {
  const exp = Math.floor(Math.log10(range || 1));
  const f = (range || 1) / 10 ** exp;
  let nf;
  if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * 10 ** exp;
};

// Rounded-top bar (flat bottom) — same shape as PurchaseSalesBarChart
// (Composants-2 §12 : coins hauts 3px en mode clair).
const barPath = (bx, by, bw, bh, radius = 8) => {
  const r = Math.min(bw / 2, bh, radius);
  if (bh <= 0) return '';
  return `M ${bx},${(by + bh).toFixed(1)} L ${bx},${(by + r).toFixed(1)} `
    + `Q ${bx},${by.toFixed(1)} ${(bx + r).toFixed(1)},${by.toFixed(1)} `
    + `L ${(bx + bw - r).toFixed(1)},${by.toFixed(1)} `
    + `Q ${(bx + bw).toFixed(1)},${by.toFixed(1)} ${(bx + bw).toFixed(1)},${(by + r).toFixed(1)} `
    + `L ${(bx + bw).toFixed(1)},${(by + bh).toFixed(1)} Z`;
};

// Jours de la semaine complétés (CA 0 pour les jours absents) — partagé par le
// graphe et par `bestWeekday` ci-dessous.
const toDays = (rows) => {
  const byWd = {};
  (Array.isArray(rows) ? rows : []).forEach((r) => { byWd[r.weekday] = r; });
  return WEEKDAYS.map((d) => ({
    ...d,
    ca: Number(byWd[d.wd]?.ca) || 0,
    count: Number(byWd[d.wd]?.count) || 0,
  }));
};

// « Meilleur jour » de la semaine — même calcul et même format `k` que
// l'annotation du graphe, pour l'afficher AILLEURS (entête de carte,
// owner 2026-09-07). Renvoie null sans données.
export const bestWeekday = (rows) => {
  const days = toDays(rows);
  const best = Math.max(0, ...days.map((d) => d.ca));
  if (!best) return null;
  const d = days.find((x) => x.ca === best);
  return { label: d?.label, ca: best, tick: fmtTick(best) };
};

const WeekdayPanelChart = ({ rows, mode = 'bars', transparent = false, light = false, caption = true, height = 460 }) => {
  const H = height;
  const PLOT = { x: M.left, y: M.top, w: W - M.left - M.right, h: H - M.top - M.bottom };
  const days = toDays(rows);

  if (days.every((d) => d.ca === 0)) {
    return (
      <div className={`flex flex-col items-center justify-center h-72 rounded-2xl ${
        light ? 'bg-gray-50 text-gray-400' : 'text-gray-400'
      }`} style={light ? undefined : { background: KI.darkBg }}>
        <p className="text-sm">Aucune vente sur la période</p>
      </div>
    );
  }

  // `light` : pas de boîtier sombre — aplat sans halo, grille pointillée
  // #F3F4F6, échelles 10.5px #9CA3AF (Composants-2 §12).
  const gridStroke = light ? KI.ramps.gray[100] : KI.pureWhite;
  const gridOp = (t) => (t === 0 ? (light ? 1 : 0.32) : (light ? 1 : 0.14));
  const gridDash = '4 6';
  const tickFill = light ? KI.faint : KI.ramps.gray[400];
  const tickSize = 15;
  const flat = transparent || light;
  const glow = flat ? undefined : 'url(#wkp-glow)';
  const bestFill = light ? KI.spruce : KI.pureWhite;
  const bestDayFill = light ? KI.ink : KI.borderLight;

  const rawMax = Math.max(1, ...days.map((d) => d.ca));
  const step = niceNum(niceNum(rawMax, false) / 4, true) || 1;
  const yMax = Math.ceil(rawMax / step) * step;
  const ticks = [];
  for (let v = 0; v <= yMax + step / 2; v += step) ticks.push(v);

  const y = (v) => PLOT.y + PLOT.h * (1 - v / (yMax || 1));
  const baseY = y(0);
  const groupW = PLOT.w / days.length;
  const cx = (i) => PLOT.x + i * groupW + groupW / 2;
  const barW = groupW * 0.42;
  const best = Math.max(...days.map((d) => d.ca));

  const pts = days.map((d, i) => [cx(i), y(d.ca)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L ${pts[pts.length - 1][0].toFixed(1)},${baseY.toFixed(1)} L ${pts[0][0].toFixed(1)},${baseY.toFixed(1)} Z`;

  return (
    <div>
      {/* légende AU-DESSUS du graphe (Brand Kit §05) : puce carrée 9px */}
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-1.5 rounded-full" style={{ background: COLOR }} />
          CA
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label={mode === 'curve'
          ? "Courbe du CA par jour de la semaine, avec échelle montant"
          : "Barres du CA par jour de la semaine, avec échelle montant"}
      >
        <defs>
          <linearGradient id="wkp-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={KI.darkCard} />
            <stop offset="1" stopColor={KI.black} />
          </linearGradient>
          <radialGradient id="wkp-sheen" cx="0.5" cy="0" r="0.95">
            <stop offset="0" stopColor={KI.pureWhite} stopOpacity="0.12" />
            <stop offset="1" stopColor={KI.pureWhite} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="wkp-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={COLOR} stopOpacity="1" />
            <stop offset="1" stopColor={COLOR} stopOpacity="0.45" />
          </linearGradient>
          <linearGradient id="wkp-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={COLOR} stopOpacity="0.42" />
            <stop offset="1" stopColor={COLOR} stopOpacity="0" />
          </linearGradient>
          <filter id="wkp-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={COLOR} floodOpacity="0.85" />
          </filter>
          <clipPath id="wkp-clip">
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} />
          </clipPath>
        </defs>

        {/* The "case": dark beveled panel — skipped in `light` mode. */}
        {!transparent && !light && (
          <>
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} fill="url(#wkp-bg)" />
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} fill="url(#wkp-sheen)" />
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h}
              fill="none" stroke={KI.pureWhite} strokeOpacity="0.10" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
            <line x1={PLOT.x + 14} y1={PLOT.y + 1} x2={PLOT.x + PLOT.w - 14} y2={PLOT.y + 1}
              stroke={KI.pureWhite} strokeOpacity="0.18" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          </>
        )}

        {/* Y échelle (montant) */}
        {ticks.map((t, i) => (
          <g key={`t${i}`}>
            <line x1={PLOT.x} y1={y(t)} x2={PLOT.x + PLOT.w} y2={y(t)}
              stroke={gridStroke} strokeOpacity={gridOp(t)}
              strokeDasharray={t === 0 ? '0' : gridDash}
              vectorEffect="non-scaling-stroke" />
            <text x={PLOT.x - 12} y={y(t) + 4} textAnchor="end"
              fontSize={tickSize} fill={tickFill}>{fmtTick(t)}</text>
          </g>
        ))}

        {mode === 'curve' ? (
          <g>
            <g clipPath="url(#wkp-clip)">
              {!light && <path d={area} fill="url(#wkp-area)" />}
              <path d={line} fill="none" stroke={COLOR} strokeWidth="3"
                strokeLinejoin="miter" strokeLinecap="butt"
                filter={glow} vectorEffect="non-scaling-stroke" />
            </g>
            {days.map((d, i) => (
              <g key={d.wd}>
                <title>{`${d.label} — ${d.ca.toFixed(3)} DT · ${d.count} vente(s)`}</title>
                <circle cx={cx(i)} cy={y(d.ca)} r={d.ca === best ? 7 : 5} fill={COLOR}
                  stroke={KI.pureWhite} strokeWidth="2" vectorEffect="non-scaling-stroke"
                  filter={glow} />
                <text x={cx(i)} y={y(d.ca) - 14} textAnchor="middle" fontSize="14"
                  fontWeight="700" fill={d.ca === best ? bestFill : COLOR}>{fmtTick(d.ca)}</text>
              </g>
            ))}
          </g>
        ) : (
          <g>
            <g clipPath="url(#wkp-clip)">
              {days.map((d, i) => {
                const top = y(d.ca);
                const bh = Math.max(0, baseY - top);
                return (
                  <path key={d.wd} d={barPath(cx(i) - barW / 2, top, barW, bh, 8)}
                    fill={light ? COLOR : 'url(#wkp-fill)'} filter={glow}
                    opacity={d.ca === best ? 1 : 0.85}>
                    <title>{`${d.label} — ${d.ca.toFixed(3)} DT · ${d.count} vente(s)`}</title>
                  </path>
                );
              })}
            </g>
            {days.map((d, i) => {
              if (d.ca <= 0) return null;
              const top = y(d.ca);
              const roomAbove = top - 7 >= PLOT.y + 14;
              return (
                <text key={`v${d.wd}`} x={cx(i)} y={roomAbove ? top - 7 : top + 18}
                  textAnchor="middle" fontSize="14" fontWeight="700"
                  fill={d.ca === best
                    ? (roomAbove ? bestFill : KI.pureWhite)
                    : (roomAbove ? COLOR : KI.pureWhite)}>
                  {fmtTick(d.ca)}
                </text>
              );
            })}
          </g>
        )}

        {/* X échelle (jours) — labels under the panel */}
        {days.map((d, i) => (
          <text key={`d${d.wd}`} x={cx(i)} y={H - M.bottom + 26} textAnchor="middle"
            fontSize={tickSize} fontWeight={d.ca === best ? 700 : 400}
            fill={d.ca === best ? bestDayFill : tickFill}>{d.label}</text>
        ))}
      </svg>

      {/* « Meilleur jour » annoté SOUS le graphe (11.5px #6B7280, mt 4 —
          Brand Kit §05). `caption={false}` le retire quand la page l'affiche
          elle-même dans l'entête de la carte (Statistiques de ventes). */}
      {caption && (
        <div className="mt-3 text-xs text-gray-500">
          <IcCalendar className="inline align-[-2px] mr-1" />Meilleur jour : <span className="font-semibold text-gray-900">{days.find((d) => d.ca === best)?.label}</span>
          <span className="tabular-nums"> ({fmtTick(best)})</span>
        </div>
      )}
    </div>
  );
};

export default WeekdayPanelChart;
